import { ASSET_CATEGORIES, isAnchorCategory, type AssetInstance } from '@/types/assetTypes';
import { formatVector3String, parseVector3String } from '@/utils/numberUtils';
import type { RootState } from '@/store/rootState';
import type { TransformEdit } from '@/modules/rendererBridge';
import type { Keyframe } from './types';
import { samePose } from './samePose';

export const assetCategoryIn = (state: RootState, assetInstanceId: string): string | null | undefined =>
  (state.sceneAssets.sceneAssetsArray as AssetInstance[]).find(a => a.instanceId === assetInstanceId)?.instanceCategory;

type Vec3 = { x: number; y: number; z: number };

export interface KeyframeTransform {
  position?: Vec3;
  rotation?: Vec3;
  scale?: Vec3;
}

/**
 * The part of a transform write a keyframe of `category` takes. Every AR client puts the scene on its anchor at
 * identity, so a geospatial or image anchor's keyframe never moves and a scan's only changes height; the backend
 * holds the same rule (AnchorKeyframes). `stored` supplies the scan's ground position.
 */
export function allowedKeyframeTransform(
  category: string | null | undefined,
  stored: KeyframeTransform | null | undefined,
  requested: KeyframeTransform,
): KeyframeTransform {
  if (!isAnchorCategory(category)) return requested;
  if (category !== ASSET_CATEGORIES.IMMERSAL || !requested.position) return {};
  return { position: { x: stored?.position?.x ?? 0, y: requested.position.y, z: stored?.position?.z ?? 0 } };
}

/** Whether the write would change anything the rule lets through; a held write is dropped rather than sent. */
export function isHeldWrite(requested: KeyframeTransform, allowed: KeyframeTransform): boolean {
  return (!!requested.position || !!requested.rotation || !!requested.scale)
    && !allowed.position && !allowed.rotation && !allowed.scale;
}

/** A new anchor keyframe's transform: the anchor itself, a scan at its requested height. */
export function placedKeyframeTransform(category: string | null | undefined, requested: Required<KeyframeTransform>): Required<KeyframeTransform> {
  if (!isAnchorCategory(category)) return requested;
  const identity = { position: { x: 0, y: 0, z: 0 }, rotation: { x: 0, y: 0, z: 0 }, scale: { x: 1, y: 1, z: 1 } };
  return category === ASSET_CATEGORIES.IMMERSAL ? { ...identity, position: { x: 0, y: requested.position.y, z: 0 } } : identity;
}

/**
 * Whether a new keyframe of `category` keeps the pose the editor saved over where the renderer places it. A scan is
 * left out: the renderer works out its height and reports it as a placement.
 */
export function anchorHoldsPlacement(category: string | null | undefined): boolean {
  return isAnchorCategory(category) && category !== ASSET_CATEGORIES.IMMERSAL;
}

interface EditorAssets {
  keyframe(keyframeId: string): ({ assetInstanceId: string } & KeyframeTransform) | undefined;
  category(assetInstanceId: string): string | null | undefined;
}

let editor: EditorAssets | null = null;

/** The open editor's keyframes and assets, which writers outside Redux check their writes against. */
export function followEditorAssets(next: EditorAssets | null): void {
  editor = next;
}

export function categoryOfAsset(assetInstanceId: string): string | null | undefined {
  return editor?.category(assetInstanceId);
}

/** `requested` as the keyframe's anchor lets it through; a keyframe the editor does not hold is left to the backend. */
export function allowedForKeyframe(keyframeId: string, requested: KeyframeTransform): KeyframeTransform {
  const held = editor?.keyframe(keyframeId);
  if (!held) return requested;
  return allowedKeyframeTransform(editor?.category(held.assetInstanceId), held, requested);
}

type TransformFields = { position?: string | null; rotation?: string | null; scale?: string | null };

/** A v1 keyframe body's transform fields as the keyframe's anchor lets them through; other fields untouched. */
export function allowedKeyframeFields<T extends TransformFields>(keyframeId: string, fields: T): T {
  const read = (text: string | null | undefined) => (text ? parseVector3String(text) ?? undefined : undefined);
  const requested = { position: read(fields.position), rotation: read(fields.rotation), scale: read(fields.scale) };
  const allowed = allowedForKeyframe(keyframeId, requested);
  if (allowed === requested) return fields;
  const rest = Object.fromEntries(Object.entries(fields).filter(([key]) => key !== 'position' && key !== 'rotation' && key !== 'scale'));
  return { ...rest, ...(allowed.position ? { position: formatVector3String(allowed.position, 3) } : {}) } as T;
}

/**
 * The renderer's reported keyframe moves, an anchor's cut to what it allows: one held whole is sent back to the renderer
 * as the editor holds it, a scan's keeps only its height. A keyframe the editor does not hold yet (a new anchor's,
 * before its keyframes load) is left to the backend's rule.
 */
export function anchoredTransformEdits(state: RootState, edits: TransformEdit[], resend: (keyframe: Keyframe) => void): TransformEdit[] {
  const read = (text: string) => parseVector3String(text) ?? undefined;
  const kept: TransformEdit[] = [];
  for (const edit of edits) {
    const held = state.keyframes.entities[edit.uniqueId];
    const category = held && assetCategoryIn(state, held.assetInstanceId);
    if (!held || !isAnchorCategory(category)) {
      kept.push(edit);
      continue;
    }
    const requested = { position: read(edit.position), rotation: read(edit.rotation), scale: read(edit.scale) };
    const allowed = allowedKeyframeTransform(category, held, requested);
    if (!allowed.position) {
      resend(held);
      continue;
    }
    const corrected = { ...held, position: allowed.position };
    if (!samePose(requested, corrected)) resend(corrected);
    kept.push({
      ...edit,
      position: formatVector3String(allowed.position, 3),
      rotation: formatVector3String(held.rotation ?? { x: 0, y: 0, z: 0 }, 3),
      scale: formatVector3String(held.scale ?? { x: 1, y: 1, z: 1 }, 3),
    });
  }
  return kept;
}
